// Single source of truth for every project shown on the landing page,
// /products and /products/:slug.
//
// To add a project: append an object below. Nothing else needs to change.
// `art` picks the small illustration on the card (quiz | exam | erp | events);
// leave it out for a plain icon card.
// Screenshots: drop images in public/projects/<slug>/ and list them in `images`
// (e.g. ['/projects/my-app/1.png']). With no images, a placeholder is shown.
// `cover` (optional): hero background image, e.g. /projects/my-app/cover.jpg
// Screenshots may be any ratio (16:9, 9:16, ...) and open in a viewer on click.
// `links` are optional (website, playStore, appStore, github); a button appears only for the links you provide.
// Placeholder until each project has its own URL: replace `DEFAULT_LINK` in a
// project's links.website with the real address.
const DEFAULT_LINK = 'https://pidesk.in';

export const projects = [
  {
    slug: 'learning-app',
    art: 'learning',
    name: 'Learning App',
    category: 'Education · Android',
    icon: 'smartphone',
    short: 'Live and recorded classes, notes, tests and batches in one Android app.',
    description:
      'An Android learning app where students, teachers and admins manage everything about online coaching. Teachers run live or recorded classes and share notes, students practise with tests and quizzes and browse a resource library, and institutes sell courses through paid batches with built-in payments. Video lectures are available in the mobile app, and free resource videos can be watched without buying a batch.',
    features: [
      'Student, teacher and admin roles',
      'Live and recorded video classes',
      'Notes and study material',
      'Test series and quizzes',
      'Resource library with uploads',
      'Online payments',
      'Batch buying',
      'Free resource videos'
    ],
    tech: ['Android', 'Cloud Backend', 'Video Streaming', 'Payment Gateway'],
    client: 'Coaching institutes',
    status: 'Live',
    images: [
      'https://files.msg91.com/560000/erbvbahi/eegbtkjw.png',
      'https://files.msg91.com/560000/erbvbahi/ilhibedo.png',
      'https://files.msg91.com/560000/erbvbahi/wwptrewp.png',
      'https://files.msg91.com/560000/erbvbahi/zbnfjaty.png',
      'https://files.msg91.com/560000/erbvbahi/pqqdpatr.png',
      'https://files.msg91.com/560000/erbvbahi/smtgdgrg.png',
      'https://files.msg91.com/560000/erbvbahi/nifptgma.png',
      'https://files.msg91.com/560000/erbvbahi/maonegkq.png',
      'https://files.msg91.com/560000/erbvbahi/vrevgipg.png',
      'https://files.msg91.com/560000/erbvbahi/tltvuvub.png',
      'https://files.msg91.com/560000/erbvbahi/qlczjaua.png'
    ],
    links: {} // add playStore: 'https://play.google.com/store/apps/details?id=...' when published
  },
  {
    slug: 'learning-web-portal',
    art: 'portal',
    name: 'Learning Web Portal',
    category: 'Education · Website',
    icon: 'globe',
    short: 'A website for institutes: student login, test series, resources and batches.',
    description:
      'A website that gives institutes their own online portal to manage students. Students log in to attempt test series, access study resources and buy batches, while institutes manage content, students and payments from an admin panel. Video lectures are delivered through the mobile app, while the website focuses on tests, resources and batch purchases.',
    features: [
      'Student login and profiles',
      'Test series and quizzes',
      'Resource access',
      'Batch buying with online payments',
      'Free resources for everyone',
      'Admin panel for institutes'
    ],
    tech: ['React', 'Cloud Hosting', 'Payment Gateway'],
    client: 'Coaching institutes',
    status: 'Live',
    images: [],
    links: { website: DEFAULT_LINK }
  },
  {
    slug: 'ashoka-quiz-platform',
    art: 'quiz',
    name: 'Ashoka Quiz Platform',
    category: 'Education',
    icon: 'server',
    short: 'Online quizzes and competitions with live leaderboards.',
    description:
      'An online quiz and competition system built for Ashoka Institute. Organizers can run digital quizzes, technical competitions and knowledge tests with real-time participation and automated response collection.',
    features: ['Real-time participation', 'Automated grading', 'Live leaderboards', 'Organizer dashboard'],
    tech: ['React', 'Realtime Sync', 'Cloud Hosting'],
    client: 'Ashoka Institute',
    status: 'Live',
    images: [
      'https://files.msg91.com/560000/wbhwnhmk/wnptqufv.png',
      'https://files.msg91.com/560000/wbhwnhmk/enmhdhuz.png',
      'https://files.msg91.com/560000/wbhwnhmk/wvcfatlk.png',
      'https://files.msg91.com/560000/wbhwnhmk/gzxaoilp.png',
      'https://files.msg91.com/560000/wbhwnhmk/vujgszti.png',
      'https://files.msg91.com/560000/wbhwnhmk/lfofczmv.png',
      'https://files.msg91.com/560000/wbhwnhmk/qfhxgrzk.png'
    ],
    links: { website: 'https://ashoka.pidesk.in/' }
  },
  {
    slug: 'devmeter',
    art: 'exam',
    name: 'Devmeter',
    category: 'Exams & Assessment',
    icon: 'code',
    short: 'Secure online tests and coding competitions.',
    description:
      'A live online test-taking platform in the style of large-scale exam systems like JEE/NTA. Conducts secure online tests, coding competitions and assessments with real-time monitoring.',
    features: ['Secure test environment', 'Coding competitions', 'Real-time monitoring', 'Proctoring ready'],
    tech: ['React', 'Realtime Sync', 'Cloud Hosting'],
    client: 'Pidesk',
    status: 'Live',
    images: [],
    links: { website: DEFAULT_LINK }
  },
  {
    slug: 'college-erp-core',
    art: 'erp',
    name: 'College ERP Core',
    category: 'Education',
    icon: 'dashboard',
    short: 'Admissions, academics and admin in one campus system.',
    description:
      'A comprehensive campus management system handling admissions, academics and administration natively, so institutions can run day-to-day operations from a single dashboard.',
    features: ['Admissions management', 'Academic records', 'Admin dashboard', 'Role-based access'],
    tech: ['React', 'REST APIs', 'Cloud Hosting'],
    client: 'Colleges & institutes',
    status: 'In development',
    images: [],    
    links: { website: DEFAULT_LINK }
  },
  {
    slug: 'dcic-platform',
    art: 'events',
    name: 'DCIC Platform',
    category: 'Events',
    icon: 'calendar',
    short: 'Ticketing, scheduling and certificates for events.',
    description:
      'An end-to-end event management platform with ticketing, scheduling and certificate distribution, built to power hackathons and community events.',
    features: ['Ticketing', 'Event scheduling', 'Hackathon support', 'Certificate distribution'],
    tech: ['React', 'REST APIs', 'Cloud Hosting'],
    client: 'DCIC Community',
    status: 'Live',
    images: [],
    links: { website: 'https://dcic.pidesk.in' }
  }
];

export const getProject = (slug) => projects.find((p) => p.slug === slug);
